import { List, Record } from "immutable";
import { CurrencyFormat, PercentageFormat } from "common/helpers/Format";
import { Nullable } from "common/types";
import { DiscountBreakdown, IDiscountBreakdown, discountBreakdownFromJSON } from "common/records/DiscountBreakdown";
import { checkDate } from "common/helpers/Date";
import { DateStringNumber } from ".";

export const discountFromJSON = (json: Partial<IDiscountData>): DiscountRecord => {
  const data = { ...json } as IDiscountRecord;

  if (data.breakdown) {
    data.breakdown = discountBreakdownFromJSON(data.breakdown);
  }

  if (data.created_at) {
    data.created_at = checkDate(data.created_at);
  }

  if (data.updated_at) {
    data.updated_at = checkDate(data.updated_at);
  }

  return new DiscountRecord(data);
};

export type DiscountKind = "percent" | "flat" | "percent_limit";
export const DiscountKinds: List<DiscountKind> = List(["percent", "flat", "percent_limit"]);

export const formatAmount = (discount: DiscountRecord | undefined) => {
  if (!discount) return "";

  const { kind, amount, limit } = discount.breakdown;

  switch (kind) {
    case "percent":
      return `${amount}%`;
    case "percent_limit":
      return `${amount}% up to ${CurrencyFormat(limit || 0, 0)}`;
    case "flat":
      return CurrencyFormat(amount || 0);
    default:
      return "";
  }
};

export interface IDiscountData {
  id: number;
  org_id: number;
  name: string;
  description: string;
  breakdown: IDiscountBreakdown;
  sort_order: number;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
}

export interface IDiscountRecord {
  id: number;
  org_id: Nullable<number>;
  name: string;
  description: string;
  breakdown: DiscountBreakdown;
  sort_order: Nullable<number>;

  created_at: Nullable<Date>;
  updated_at: Nullable<Date>;
}

const defaultDiscountProps: IDiscountRecord = {
  id: -1,
  org_id: null,
  name: "",
  description: "",
  breakdown: new DiscountBreakdown(),
  sort_order: null,

  created_at: null,
  updated_at: null,
};

export const IDiscountFieldLabel = {
  flat: "Amount",
  percent_limit: "Percentage",
  percent: "Percentage",
};

export class DiscountRecord extends Record(defaultDiscountProps) implements IDiscountRecord {}

export const formatKind = (type: DiscountKind): string => {
  switch (type) {
    case "percent":
      return "Percentage Off";
    case "flat":
      return "Flat Amount";
    case "percent_limit":
      return "Percentage up to $$";
    default:
      return "";
  }
};

export const discountName = (discount: DiscountRecord) => {
  const amount = discount.getIn(["breakdown", "amount"]) || 0;
  const kind = discount.getIn(["breakdown", "kind"]);
  const discountSuffix = ["percent", "percent_limit"].includes(kind) ? ` (${amount}%)` : "";
  return `${discount.get("name")}${discountSuffix}`;
};
